import { useMemo } from "react";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { ActiveUser } from "@/hooks/use-active-users";
import { getGuestIdentity, isAnonymousUserId } from "@/lib/guest-identity";

interface AvatarGroupProps {
  activeUsers: ActiveUser[];
  onActiveUsersClick?: () => void;
  className?: string;
  maxVisible?: number;
  size?: "xs" | "sm";
}

export const AvatarGroup = ({
  activeUsers,
  onActiveUsersClick,
  className,
  maxVisible = 3,
  size = "sm",
}: AvatarGroupProps) => {
  const authenticatedUsers = useMemo(() => {
    return activeUsers.filter((user) => !isAnonymousUserId(user.userId));
  }, [activeUsers]);

  const anonymousUsers = useMemo(() => {
    return activeUsers.filter((user) => isAnonymousUserId(user.userId));
  }, [activeUsers]);

  const visibleUsers = useMemo(() => {
    const usersToShow = [...authenticatedUsers];
    if (usersToShow.length < maxVisible && anonymousUsers.length > 0) {
      const remainingSlots = maxVisible - usersToShow.length;
      usersToShow.push(...anonymousUsers.slice(0, remainingSlots));
    }
    return usersToShow.slice(0, maxVisible);
  }, [authenticatedUsers, anonymousUsers, maxVisible]);

  const remainingCount = useMemo(() => {
    return Math.max(0, activeUsers.length - visibleUsers.length);
  }, [activeUsers.length, visibleUsers.length]);

  if (activeUsers.length === 0) {
    return null;
  }

  const content = (
    <>
      <div className="flex items-center">
        {visibleUsers.map((user, index) => {
          const guest = getGuestIdentity(user.userId);
          return (
            <div
              key={user.userId || `anonymous-${index}`}
              className="relative"
              style={{
                zIndex: visibleUsers.length - index,
                marginLeft: index > 0 ? "-0.75rem" : "0",
              }}
            >
              <UserAvatar
                userId={guest ? null : user.userId}
                displayName={user.displayName || guest?.displayName}
                email={user.email}
                avatarUrl={guest ? null : user.avatarUrl}
                size={size}
                className="border-2 border-background"
                fallbackStyle={guest?.avatarStyle}
              />
            </div>
          );
        })}
      </div>
      {remainingCount > 0 && (
        <Badge
          variant="secondary"
          className="text-xs h-5 px-1.5 min-w-[1.25rem] flex items-center justify-center"
        >
          +{remainingCount}
        </Badge>
      )}
    </>
  );

  if (onActiveUsersClick) {
    return (
      <button
        onClick={onActiveUsersClick}
        className={cn(
          "flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer",
          className
        )}
        title={`${activeUsers.length} active ${activeUsers.length === 1 ? "user" : "users"}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={cn("flex items-center gap-2", className)}
      aria-hidden="true"
    >
      {content}
    </div>
  );
};
